import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Lock, Unlock, PlusLg, Eye, EyeSlash, Clipboard, ClipboardCheck, Upload, Search, Dice5, ShieldLock, BoxArrowUpRight } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, Modal, RowActions } from '../components/ui.jsx'
import { createVault, unlockVault, encrypt, decrypt, generatePassword, strength } from '../lib/vault.js'
import { setAt, updateAt, removeAt } from '../lib/firebase.js'

const AUTO_LOCK_MS = 5 * 60 * 1000
const CLIPBOARD_CLEAR_MS = 30 * 1000
const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8)

export default function Passwords() {
  const data = useData()
  const header = data.vault?.header
  const [key, setKey] = useState(null)
  const [entries, setEntries] = useState(null) // decrypted, in memory only while unlocked
  const lock = useCallback(() => { setKey(null); setEntries(null) }, [])

  // Decrypt whenever the encrypted entries change (e.g. edits from another device).
  const blobs = data.vault?.entries
  useEffect(() => {
    if (!key) return
    let cancelled = false
    Promise.all(Object.entries(blobs || {}).map(async ([id, b]) => ({ id, ...(await decrypt(key, b)) })))
      .then((list) => !cancelled && setEntries(list.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'hu'))))
      .catch(() => !cancelled && lock())
    return () => { cancelled = true }
  }, [key, blobs, lock])

  // Auto-lock after inactivity.
  useEffect(() => {
    if (!key) return
    let timer = setTimeout(lock, AUTO_LOCK_MS)
    const reset = () => { clearTimeout(timer); timer = setTimeout(lock, AUTO_LOCK_MS) }
    const events = ['pointerdown', 'keydown', 'scroll']
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, reset)) }
  }, [key, lock])

  if (!header) return <Setup onCreated={setKey} />
  if (!key) return <UnlockForm header={header} onUnlock={setKey} />
  return <Vault vaultKey={key} entries={entries} onLock={lock} header={header} onRekey={setKey} />
}

function Intro() {
  return (
    <p className="small text-body-secondary">
      Entries are encrypted in this browser with your master password (AES-256-GCM, PBKDF2 600 000 rounds) before they are saved.
      The master password is never stored or sent anywhere, so nobody, including the database owner, can read the vault without it.
      <b> If you forget it, the vault cannot be recovered.</b>
    </p>
  )
}

function Setup({ onCreated }) {
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const score = strength(pw)

  async function submit(e) {
    e.preventDefault()
    if (pw.length < 12) return setErr('Use at least 12 characters.')
    if (pw !== pw2) return setErr('The two passwords do not match.')
    setBusy(true)
    const { key, header } = await createVault(pw)
    await setAt('vault/header', header)
    onCreated(key)
  }

  return (
    <>
      <PageHeader title="Passwords" subtitle="Create your encrypted vault" />
      <Panel className="mx-auto" >
        <Intro />
        <form onSubmit={submit} style={{ maxWidth: 420 }}>
          <p className="small">Choose a master password <b>different from your login password</b>. A long phrase is easiest to remember.</p>
          <label className="form-label small" htmlFor="mp1">Master password</label>
          <input id="mp1" type="password" className="form-control" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <div className="progress my-2" style={{ height: 4 }}>
            <div className={`progress-bar ${['bg-danger', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'][score]}`} style={{ width: `${(score / 4) * 100}%` }} />
          </div>
          <label className="form-label small" htmlFor="mp2">Repeat master password</label>
          <input id="mp2" type="password" className="form-control mb-3" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          {err && <div className="alert alert-danger py-2 small">{err}</div>}
          <button className="btn btn-primary" disabled={busy}><ShieldLock /> {busy ? 'Creating…' : 'Create vault'}</button>
        </form>
      </Panel>
    </>
  )
}

function UnlockForm({ header, onUnlock }) {
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [reset, setReset] = useState(false)
  const [confirm, setConfirm] = useState('')

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setErr(null)
    const key = await unlockVault(pw, header)
    setBusy(false)
    if (key) onUnlock(key)
    else { setErr('Wrong master password.'); setPw('') }
  }

  return (
    <>
      <PageHeader title="Passwords" subtitle="Vault is locked" />
      <Panel>
        <form onSubmit={submit} style={{ maxWidth: 420 }}>
          <label className="form-label small" htmlFor="mp">Master password</label>
          <input id="mp" type="password" className="form-control mb-3" autoComplete="current-password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} />
          {err && <div className="alert alert-danger py-2 small">{err}</div>}
          <button className="btn btn-primary" disabled={busy || !pw}><Unlock /> {busy ? 'Unlocking…' : 'Unlock'}</button>
        </form>
        <div className="mt-4 small">
          {!reset ? <button className="btn btn-link btn-sm p-0 text-body-secondary" onClick={() => setReset(true)}>Forgot the master password?</button> : (
            <div className="alert alert-warning">
              Without the master password the entries cannot be decrypted. You can only delete the vault and start over.
              Type <b>DELETE</b> to confirm:
              <div className="d-flex gap-2 mt-2">
                <input className="form-control form-control-sm" style={{ maxWidth: 140 }} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                <button className="btn btn-sm btn-danger" disabled={confirm !== 'DELETE'} onClick={() => removeAt('vault')}>Delete vault</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setReset(false); setConfirm('') }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </Panel>
    </>
  )
}

function Vault({ vaultKey, entries, onLock, header, onRekey }) {
  const [q, setQ] = useState('')
  const [edit, setEdit] = useState(null)
  const [importing, setImporting] = useState(false)
  const [changing, setChanging] = useState(false)
  const filtered = useMemo(() => (entries || []).filter((e) =>
    !q || [e.title, e.username, e.url, e.notes].some((f) => (f || '').toLowerCase().includes(q.toLowerCase()))), [entries, q])

  const save = async (entry) => {
    const { id, ...rest } = entry
    await setAt(`vault/entries/${id || newId()}`, await encrypt(vaultKey, { ...rest, updatedAt: Date.now() }))
  }

  return (
    <>
      <PageHeader title="Passwords" subtitle={entries ? `${entries.length} entries · locks after 5 minutes of inactivity` : 'Decrypting…'}>
        <button className="btn btn-outline-secondary" onClick={() => setImporting(true)}><Upload /> Import</button>
        <button className="btn btn-primary" onClick={() => setEdit({})}><PlusLg /> Entry</button>
        <button className="btn btn-outline-danger" onClick={onLock}><Lock /> Lock</button>
      </PageHeader>
      <Panel>
        <div className="input-group mb-3" style={{ maxWidth: 360 }}>
          <span className="input-group-text"><Search /></span>
          <input className="form-control" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        </div>
        {!entries ? <div className="empty">Decrypting…</div> : filtered.length ? (
          <div className="table-wrap"><table className="table align-middle">
            <thead><tr><th>Site</th><th>Username</th><th>Password</th><th /></tr></thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td>
                    <div className="fw-semibold">{e.title || <span className="text-body-secondary">(untitled)</span>}</div>
                    {e.url && <a className="small text-truncate d-inline-block" style={{ maxWidth: 240 }} href={/^https?:\/\//.test(e.url) ? e.url : `https://${e.url}`} target="_blank" rel="noreferrer noopener">{e.url} <BoxArrowUpRight size={10} /></a>}
                    {e.notes && <div className="small text-body-secondary text-truncate" style={{ maxWidth: 280 }}>{e.notes}</div>}
                  </td>
                  <td>{e.username && <span className="d-inline-flex align-items-center gap-1"><span className="small">{e.username}</span><CopyButton value={e.username} /></span>}</td>
                  <td><Secret value={e.password} /></td>
                  <td className="text-end"><RowActions onEdit={() => setEdit(e)} onDelete={() => removeAt(`vault/entries/${e.id}`)} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : <div className="empty">{entries.length ? 'No matches.' : 'The vault is empty. Add an entry or import your spreadsheet.'}</div>}
        <div className="mt-3"><button className="btn btn-link btn-sm p-0 text-body-secondary" onClick={() => setChanging(true)}>Change master password</button></div>
      </Panel>
      {edit && <EntryModal entry={edit} onSave={save} onClose={() => setEdit(null)} />}
      {importing && <ImportModal vaultKey={vaultKey} onClose={() => setImporting(false)} />}
      {changing && <ChangeMasterModal header={header} entries={entries} onDone={onRekey} onClose={() => setChanging(false)} />}
    </>
  )
}

function Secret({ value }) {
  const [shown, setShown] = useState(false)
  if (!value) return null
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <code className="text-body" style={{ fontSize: '.85rem' }}>{shown ? value : '••••••••••'}</code>
      <button className="icon-btn" title={shown ? 'Hide' : 'Show'} onClick={() => setShown(!shown)}>{shown ? <EyeSlash /> : <Eye />}</button>
      <CopyButton value={value} clear />
    </span>
  )
}

function CopyButton({ value, clear }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef()
  useEffect(() => () => clearTimeout(timer.current), [])
  async function copy() {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
    if (clear) timer.current = setTimeout(() => navigator.clipboard.writeText('').catch(() => {}), CLIPBOARD_CLEAR_MS)
  }
  return <button className="icon-btn" title={clear ? 'Copy (clipboard clears after 30 s)' : 'Copy'} onClick={copy}>{copied ? <ClipboardCheck className="text-success" /> : <Clipboard />}</button>
}

function EntryModal({ entry, onSave, onClose }) {
  const [v, setV] = useState({ title: '', username: '', password: '', url: '', notes: '', ...entry })
  const [show, setShow] = useState(!entry.id)
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value })
  async function submit(e) { e.preventDefault(); setBusy(true); await onSave(v); onClose() }
  return (
    <Modal title={entry.id ? 'Edit entry' : 'New entry'} onClose={onClose}>
      <form onSubmit={submit} className="row g-3" autoComplete="off">
        <div className="col-12"><label className="form-label small">Site / name</label><input className="form-control" required value={v.title} onChange={set('title')} /></div>
        <div className="col-6"><label className="form-label small">Username / email</label><input className="form-control" value={v.username} onChange={set('username')} /></div>
        <div className="col-6"><label className="form-label small">URL</label><input className="form-control" value={v.url} onChange={set('url')} /></div>
        <div className="col-12">
          <label className="form-label small">Password</label>
          <div className="input-group">
            <input type={show ? 'text' : 'password'} className="form-control font-monospace" autoComplete="new-password" value={v.password} onChange={set('password')} />
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShow(!show)}>{show ? <EyeSlash /> : <Eye />}</button>
            <button type="button" className="btn btn-outline-secondary" title="Generate" onClick={() => { setV({ ...v, password: generatePassword() }); setShow(true) }}><Dice5 /> Generate</button>
          </div>
        </div>
        <div className="col-12"><label className="form-label small">Notes</label><textarea className="form-control" rows={2} value={v.notes} onChange={set('notes')} /></div>
        <div className="col-12 d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy}>{busy ? 'Encrypting…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}

const FIELDS = [
  { id: 'title', label: 'Site / name', guess: /^(név|nev|oldal|site|name|title|szolgáltatás|szolgaltatas|megnevezés|megnevezes|weboldal|fiók|fiok|account|alkalmazás|app)/i },
  { id: 'username', label: 'Username / email', guess: /(felhasználó|felhasznalo|user|login|e-?mail|azonosító|azonosito)/i },
  { id: 'password', label: 'Password', guess: /(jelszó|jelszo|password|pass|pw|pin)/i },
  { id: 'url', label: 'URL', guess: /(url|link|web|cím|cim)/i },
  { id: 'notes', label: 'Notes', guess: /(megjegyzés|megjegyzes|note|comment|egyéb|egyeb|info)/i },
]

function ImportModal({ vaultKey, onClose }) {
  const fileRef = useRef()
  const [wb, setWb] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [map, setMap] = useState({})
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null)

  const rows = useMemo(() => {
    if (!wb || !sheet) return []
    return wb.XLSX.utils.sheet_to_json(wb.book.Sheets[sheet], { header: 1, blankrows: false, defval: '' })
  }, [wb, sheet])
  const headers = rows[0] || []
  const body = rows.slice(1).filter((r) => r.some((c) => String(c).trim()))

  useEffect(() => {
    const m = {}
    for (const f of FIELDS) {
      const i = headers.findIndex((h, idx) => f.guess.test(String(h)) && !Object.values(m).includes(idx))
      if (i >= 0) m[f.id] = i
    }
    setMap(m)
  }, [sheet, wb]) // eslint-disable-line react-hooks/exhaustive-deps

  async function onFile(e) {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    setErr(null)
    try {
      const XLSX = await import('xlsx')
      // CSV must be read as text, otherwise accented characters (Jelszó, Felhasználónév…) get garbled.
      const book = /\.csv$/i.test(file.name)
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer())
      setWb({ XLSX, book, name: file.name })
      setSheet(book.SheetNames[0])
    } catch (e2) {
      setErr(/password-protected/i.test(e2.message)
        ? 'This file is password-protected, which the browser cannot open. In Excel: File → Info → Protect Workbook → Encrypt with Password → clear the password, then Save As a temporary copy. Import that copy here and delete it afterwards.'
        : e2.message)
    }
  }

  async function run() {
    setBusy(true)
    const patch = {}
    let n = 0
    for (const r of body) {
      const entry = { updatedAt: Date.now() }
      for (const f of FIELDS) entry[f.id] = map[f.id] != null ? String(r[map[f.id]] ?? '').trim() : ''
      if (!entry.title && !entry.username && !entry.password) continue
      if (!entry.title) entry.title = entry.url || entry.username
      patch[newId() + n] = await encrypt(vaultKey, entry)
      n++
    }
    await updateAt('vault/entries', patch)
    setBusy(false)
    setDone(n)
  }

  const cell = (r, f) => (map[f] == null ? '' : f === 'password' ? (String(r[map[f]] || '') ? '••••••' : '') : String(r[map[f]] ?? ''))

  return (
    <Modal title="Import passwords" onClose={onClose}>
      {done != null ? (
        <>
          <div className="alert alert-success">Encrypted and saved {done} entries.</div>
          <p className="small">Now <b>delete any unprotected copy</b> of the spreadsheet you made for the import.</p>
          <div className="text-end"><button className="btn btn-primary" onClick={onClose}>Done</button></div>
        </>
      ) : !wb ? (
        <>
          <p className="small text-body-secondary">The file is read and encrypted only in this browser; its contents are never uploaded unencrypted. Supported: .xlsx, .xls, .csv (without a file password).</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
          <button className="btn btn-primary" onClick={() => fileRef.current.click()}><Upload /> Choose file</button>
          {err && <div className="alert alert-warning mt-3 small mb-0">{err}</div>}
        </>
      ) : (
        <>
          {wb.book.SheetNames.length > 1 && (
            <div className="mb-3"><label className="form-label small">Sheet</label>
              <select className="form-select" value={sheet} onChange={(e) => setSheet(e.target.value)}>{wb.book.SheetNames.map((s) => <option key={s}>{s}</option>)}</select></div>
          )}
          <p className="small text-body-secondary">Match the columns (first row = headers):</p>
          <div className="row g-2 mb-3">
            {FIELDS.map((f) => (
              <div key={f.id} className="col-6">
                <label className="form-label small mb-1">{f.label}</label>
                <select className="form-select form-select-sm" value={map[f.id] ?? ''} onChange={(e) => setMap({ ...map, [f.id]: e.target.value === '' ? undefined : Number(e.target.value) })}>
                  <option value="">—</option>
                  {headers.map((h, i) => <option key={i} value={i}>{String(h) || `Column ${i + 1}`}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="small mb-2">{body.length} rows. Preview:</div>
          <div className="table-wrap mb-3"><table className="table table-sm small">
            <thead><tr>{FIELDS.map((f) => <th key={f.id}>{f.label}</th>)}</tr></thead>
            <tbody>{body.slice(0, 3).map((r, i) => <tr key={i}>{FIELDS.map((f) => <td key={f.id} className="text-truncate" style={{ maxWidth: 110 }}>{cell(r, f.id)}</td>)}</tr>)}</tbody>
          </table></div>
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy || map.password == null} onClick={run}>{busy ? 'Encrypting…' : `Encrypt & import ${body.length}`}</button>
          </div>
        </>
      )}
    </Modal>
  )
}

function ChangeMasterModal({ header, entries, onDone, onClose }) {
  const [cur, setCur] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  async function submit(e) {
    e.preventDefault()
    setErr(null)
    if (pw.length < 12) return setErr('Use at least 12 characters.')
    if (pw !== pw2) return setErr('The new passwords do not match.')
    setBusy(true)
    if (!(await unlockVault(cur, header))) { setBusy(false); return setErr('Current master password is wrong.') }
    const { key, header: next } = await createVault(pw)
    const reEncrypted = {}
    for (const { id, ...rest } of entries) reEncrypted[id] = await encrypt(key, rest)
    await setAt('vault', { header: next, entries: reEncrypted })
    onDone(key)
    onClose()
  }
  return (
    <Modal title="Change master password" onClose={onClose}>
      <form onSubmit={submit} className="row g-3">
        <div className="col-12"><label className="form-label small">Current master password</label><input type="password" className="form-control" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
        <div className="col-6"><label className="form-label small">New master password</label><input type="password" className="form-control" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
        <div className="col-6"><label className="form-label small">Repeat</label><input type="password" className="form-control" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
        {err && <div className="col-12"><div className="alert alert-danger py-2 small mb-0">{err}</div></div>}
        <div className="col-12 d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy}>{busy ? 'Re-encrypting…' : 'Change'}</button>
        </div>
      </form>
    </Modal>
  )
}
